import { useCallback, useEffect, useMemo, useState } from "react";

import {
  getDriverAccessAccounts,
  saveDriverAccessAccount,
} from "../../api/client.js";
import { useSortableData } from "../../hooks/useSortableData.js";
import { Dialog } from "../Dialog.jsx";
import { SortableHeader } from "../SortableHeader.jsx";
import {
  DataMasterActiveFilters,
  DataMasterKpiCard,
  DataMasterKpiStrip,
  DataMasterRecordFooter,
  DataMasterSection,
  DataMasterTableShell,
  DataMasterToolbar,
} from "./DataMasterLayout.jsx";

function formatDateTime(value) {
  if (!value) return "Belum pernah";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function AccessBadge({ active, configured }) {
  const label = !configured ? "Belum dibuat" : active ? "Aktif" : "Nonaktif";
  const tone = !configured ? "neutral" : active ? "yes" : "no";

  return <span className={`master-active master-active--${tone}`}>{label}</span>;
}

function RoleBadge({ principalType }) {
  return (
    <span className="dm-role-badge">
      {principalType === "DRIVER" ? "Driver" : "Co-driver"}
    </span>
  );
}

export function DriverAccessTab() {
  const emptyFilters = { search: "", role: "", access: "", login: "" };
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: "", pin: "", active: true });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const payload = await getDriverAccessAccounts();
      setRows(payload.accounts || []);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const keyword = filters.search.trim().toLowerCase();

    return rows.filter((row) => {
      if (
        keyword &&
        ![row.displayName, row.username, row.principalType]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(keyword))
      ) {
        return false;
      }

      if (filters.role && row.principalType !== filters.role) return false;
      if (filters.access === "ACTIVE" && (!row.id || !row.accountActive)) return false;
      if (filters.access === "INACTIVE" && (!row.id || row.accountActive)) return false;
      if (filters.access === "MISSING" && row.id) return false;
      if (filters.login === "USED" && !row.lastLoginAt) return false;
      if (filters.login === "NEVER" && row.lastLoginAt) return false;

      return true;
    });
  }, [filters, rows]);

  const summary = useMemo(
    () => ({
      total: filteredRows.length,
      active: filteredRows.filter((row) => row.id && row.accountActive).length,
      missing: filteredRows.filter((row) => !row.id).length,
      neverLogin: filteredRows.filter((row) => !row.lastLoginAt).length,
      inactiveMaster: filteredRows.filter((row) => !row.principalActive).length,
    }),
    [filteredRows],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(filteredRows, "displayName");

  function openEditor(row) {
    setEditing(row);
    setForm({
      username: row.username || "",
      pin: "",
      active: row.id ? row.accountActive : true,
    });
    setError("");
    setMessage("");
  }

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const payload = {
        username: form.username,
        active: form.active,
      };

      if (form.pin) payload.pin = form.pin;

      const response = await saveDriverAccessAccount(
        editing.principalType,
        editing.principalId,
        payload,
      );

      setMessage(response.message);
      setEditing(null);
      await load();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  function applyFilters(event) {
    event.preventDefault();
    setFilters(draftFilters);
  }

  function clearFilters() {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
  }

  return (
    <DataMasterSection
      error={error}
      message={message}
      subtitle="Kelola username, PIN, status akses, dan aktivitas login driver serta co-driver Sedulur Dalan."
      title="Akses Sedulur Dalan"
    >
      <DataMasterKpiStrip hint="Seluruh hasil filter aktif" label="Ringkasan akses Sedulur Dalan">
        <DataMasterKpiCard icon="user" label="Total pengguna" value={summary.total} />
        <DataMasterKpiCard icon="check" label="Akses aktif" tone="success" value={summary.active} />
        <DataMasterKpiCard icon="warning" label="Belum dibuat" tone="warning" value={summary.missing} />
        <DataMasterKpiCard icon="clock" label="Belum pernah login" value={summary.neverLogin} />
        <DataMasterKpiCard
          helper="Master driver/co-driver nonaktif"
          icon="close"
          label="Master nonaktif"
          value={summary.inactiveMaster}
        />
      </DataMasterKpiStrip>

      <DataMasterToolbar className="dm-workforce-filter-bar" onSubmit={applyFilters}>
        <input
          aria-label="Cari pengguna Sedulur Dalan"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, search: event.target.value }))
          }
          placeholder="Cari nama atau username"
          value={draftFilters.search}
        />
        <select
          aria-label="Filter peran pengguna"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, role: event.target.value }))
          }
          value={draftFilters.role}
        >
          <option value="">Semua peran</option>
          <option value="DRIVER">Driver</option>
          <option value="CO_DRIVER">Co-driver</option>
        </select>
        <select
          aria-label="Filter status akses"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, access: event.target.value }))
          }
          value={draftFilters.access}
        >
          <option value="">Semua status akses</option>
          <option value="ACTIVE">Aktif</option>
          <option value="INACTIVE">Nonaktif</option>
          <option value="MISSING">Belum dibuat</option>
        </select>
        <select
          aria-label="Filter aktivitas login"
          onChange={(event) =>
            setDraftFilters((current) => ({ ...current, login: event.target.value }))
          }
          value={draftFilters.login}
        >
          <option value="">Semua aktivitas login</option>
          <option value="USED">Pernah login</option>
          <option value="NEVER">Belum pernah login</option>
        </select>
        <button className="secondary-button" type="submit">Terapkan</button>
        <button className="text-button" onClick={clearFilters} type="button">Bersihkan</button>
      </DataMasterToolbar>

      <DataMasterActiveFilters
        filters={[
          { label: "Pencarian", value: filters.search },
          {
            label: "Peran",
            value: filters.role === "DRIVER" ? "Driver" : filters.role === "CO_DRIVER" ? "Co-driver" : "",
          },
          {
            label: "Akses",
            value:
              filters.access === "ACTIVE"
                ? "Aktif"
                : filters.access === "INACTIVE"
                  ? "Nonaktif"
                  : filters.access === "MISSING"
                    ? "Belum dibuat"
                    : "",
          },
          {
            label: "Login",
            value: filters.login === "USED" ? "Pernah login" : filters.login === "NEVER" ? "Belum pernah login" : "",
          },
        ]}
        onClear={clearFilters}
      />

      <DataMasterTableShell
        className="dm-table-shell--driver-access"
        emptyText="Pengguna Sedulur Dalan tidak ditemukan."
        loading={loading}
        mode="compact"
        rowCount={filteredRows.length}
      >
        <table className="master-table master-table--driver-access-compact">
          <thead>
            <tr>
              <SortableHeader column="displayName" sort={sort} toggleSort={toggleSort}>Pengguna</SortableHeader>
              <SortableHeader column="username" sort={sort} toggleSort={toggleSort}>Username</SortableHeader>
              <SortableHeader column="accountActive" sort={sort} toggleSort={toggleSort}>Status akses</SortableHeader>
              <SortableHeader column="lastLoginAt" sort={sort} toggleSort={toggleSort}>Aktivitas</SortableHeader>
              <th className="sticky-action-cell master-action-cell">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedItems.map((row) => (
              <tr key={`${row.principalType}:${row.principalId}`}>
                <td>
                  <div className="dm-cell-stack dm-cell-stack--primary">
                    <strong>{row.displayName}</strong>
                    <span className="dm-inline-metadata">
                      <RoleBadge principalType={row.principalType} />
                      {!row.principalActive && <em>Master nonaktif</em>}
                    </span>
                  </div>
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{row.username || "Belum dibuat"}</strong>
                    <span>{row.id ? "Akun sudah dikonfigurasi" : "Perlu pembuatan akses"}</span>
                  </div>
                </td>
                <td>
                  <AccessBadge active={row.accountActive} configured={Boolean(row.id)} />
                </td>
                <td>
                  <div className="dm-cell-stack">
                    <strong>{formatDateTime(row.lastLoginAt)}</strong>
                    <span>{row.lastLoginAt ? "Login terakhir" : "Belum ada aktivitas"}</span>
                  </div>
                </td>
                <td className="sticky-action-cell master-action-cell">
                  <button
                    className="table-action-button"
                    onClick={() => openEditor(row)}
                    type="button"
                  >
                    {row.id ? "Atur akses" : "Buat akses"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataMasterTableShell>

      <DataMasterRecordFooter count={filteredRows.length} />

      {editing && (
        <Dialog
          onClose={() => setEditing(null)}
          title={`${editing.id ? "Atur" : "Buat"} akses ${editing.displayName}`}
        >
          <form onSubmit={submit}>
            <div className="dialog-card__body dialog-form__grid">
              <label>
                <span>Username</span>
                <input
                  autoComplete="username"
                  onChange={(event) =>
                    setForm((current) => ({ ...current, username: event.target.value }))
                  }
                  placeholder="Nomor HP atau username"
                  required
                  value={form.username}
                />
                <small>Minimal 4 karakter, tanpa spasi.</small>
              </label>

              <label>
                <span>{editing.id ? "PIN baru (opsional)" : "PIN 6 digit"}</span>
                <input
                  autoComplete="new-password"
                  inputMode="numeric"
                  maxLength={6}
                  minLength={6}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      pin: event.target.value.replace(/\D/g, "").slice(0, 6),
                    }))
                  }
                  pattern="\d{6}"
                  placeholder="••••••"
                  required={!editing.id}
                  type="password"
                  value={form.pin}
                />
                <small>
                  {editing.id
                    ? "Kosongkan jika PIN tidak perlu diubah. Mengubah PIN akan mengeluarkan semua perangkat."
                    : "Gunakan PIN sementara yang disampaikan langsung kepada pengguna."}
                </small>
              </label>

              <label>
                <span>Status akses</span>
                <select
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      active: event.target.value === "active",
                    }))
                  }
                  value={form.active ? "active" : "inactive"}
                >
                  <option value="active">Aktif</option>
                  <option value="inactive">Nonaktif</option>
                </select>
              </label>
            </div>

            <footer className="dialog-card__actions">
              <button
                className="secondary-button"
                disabled={busy}
                onClick={() => setEditing(null)}
                type="button"
              >
                Batal
              </button>
              <button className="primary-button" disabled={busy} type="submit">
                {busy ? "Menyimpan..." : "Simpan akses"}
              </button>
            </footer>
          </form>
        </Dialog>
      )}
    </DataMasterSection>
  );
}
